import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../hooks/useLocale';
import { subscribeMailing } from '../services/mailingService';
import { EMAIL_REGEX } from '../utils/validation';
import { mailingButtonStyle } from './mailingStyles';

/**
 * Home-page sign-up for raffle / event / newsletter emails. Anonymous
 * visitors get a double opt-in email; a signed-in user subscribing their own
 * address is subscribed straight away.
 */
const MailingSignup = () => {
  const { t } = useTranslation('mailing');
  const { user, session } = useAuth();
  const locale = useLocale();
  const [email, setEmail] = useState(user?.email || '');
  const [state, setState] = useState('idle'); // idle | sending | pending | subscribed
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!EMAIL_REGEX.test(email.trim())) {
      setError(t('signup.errors.emailInvalid'));
      return;
    }
    setState('sending');
    try {
      const { status } = await subscribeMailing(
        { email, source: 'home', locale, name: user?.user_metadata?.full_name },
        session?.access_token,
      );
      setState(status === 'subscribed' ? 'subscribed' : 'pending');
    } catch {
      setState('idle');
      setError(t('signup.errors.generic'));
    }
  };

  const done = state === 'pending' || state === 'subscribed';

  return (
    <section className="home-section" style={{ padding: '2.5rem 0', textAlign: 'center', backgroundColor: '#f4f4f4' }}>
      <div className="container" style={{ maxWidth: 560 }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', fontFamily: "'Gambado Sans', sans-serif" }}>{t('signup.title')}</h2>
        <p style={{ color: '#555', marginBottom: '1.25rem' }}>{t('signup.text')}</p>
        {done ? (
          <p role="status" style={{ color: '#1a1a1a', fontWeight: 600 }}>
            {state === 'subscribed' ? t('signup.subscribed') : t('signup.pending')}
          </p>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('signup.emailPlaceholder')}
                aria-label={t('signup.emailLabel')}
                autoComplete="email"
                className="form-input"
                style={{ flex: '1 1 220px', minWidth: 0, padding: '0.65rem 1rem', borderRadius: '999px', border: '1px solid #ccc', fontSize: '1rem' }}
              />
              <button type="submit" disabled={state === 'sending'} style={mailingButtonStyle}>
                {state === 'sending' ? t('signup.submitting') : t('signup.submit')}
              </button>
            </div>
            {error && <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.75rem' }}>{error}</p>}
          </form>
        )}
        <p style={{ color: '#777', fontSize: '0.8rem', marginTop: '1rem', lineHeight: 1.5 }}>
          <Trans
            i18nKey="signup.legal"
            t={t}
            components={{ privacyLink: <Link to="/terminos-y-condiciones#aviso-de-privacidad" target="_blank" rel="noopener noreferrer" /> }}
          />
        </p>
      </div>
    </section>
  );
};

export default MailingSignup;
